// SubscriptionList.jsx

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Api from "../../service/http";

const InfoItem = ({ label, value, theme }) => (
  <div className={`flex flex-col gap-1 ${
    theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-100'
  } border rounded-2xl p-4 transition-all duration-300`}>
    <span className={`text-[10px] font-bold uppercase tracking-widest ${
      theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
    }`}>{label}</span>
    <span className={`font-bold ${
      theme === 'dark' ? 'text-slate-200' : 'text-slate-900'
    }`}>{value ?? "N/A"}</span>
  </div>
);

const SubscriptionList = () => {
  const { theme } = useSelector((state) => state.theme);
  const navigate = useNavigate();
  const { id } = useParams();

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  // The Api helper never rejects on 4xx/5xx (validateStatus returns the
  // status), so success has to be confirmed from res.status and res.data.
  const getSubscription = async () => {
    setLoading(true);
    setError(null);
    setNeedsSignIn(false);
    try {
      const res = await Api.get(`/web/subscription-info/${id}`);

      if (res?.status === 401) {
        setNeedsSignIn(true);
        return;
      }

      if (res?.status !== 200 || res?.data?.error || !res?.data?.subscription) {
        setError(
          res?.data?.message ||
            "We couldn't load this subscription. The link may have expired."
        );
        return;
      }

      setSubscription(res.data.subscription);
    } catch (e) {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleYes = () => navigate(`/unsubscribe/${id}/impact`);
  const handleNo = () => navigate("/profile");

  return (
    <div className={`flex-1 flex flex-col items-center justify-center p-4 sm:p-6 min-h-[calc(100vh-150px)] ${theme === 'dark' ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-900'}`}>
      <div className={`w-full max-w-2xl ${theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-100'} backdrop-blur-xl rounded-[2rem] p-6 sm:p-10 shadow-2xl border`}>
        <div className="flex items-center gap-3 mb-8">
          <div className="h-8 w-1.5 bg-blue-600 rounded-full" />
          <h2 className={`text-2xl sm:text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>Subscription Details</h2>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
            <div className={`text-sm font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Loading details...</div>
          </div>
        ) : needsSignIn ? (
          <div className="flex flex-col items-center justify-center py-10 gap-6 text-center">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 text-3xl">
              &#128274;
            </div>
            <p className={`text-base font-medium ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
              Please sign in to this website to manage your subscription, then
              open the cancellation link again.
            </p>
            <button
              onClick={() => navigate(`/signin?redirect=/unsubscribe/${id}`)}
              className="py-4 px-8 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-widest rounded-2xl transition-all duration-300 active:scale-95"
            >
              Sign in
            </button>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-10 gap-6 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-600 text-3xl">
              &#9888;&#65039;
            </div>
            <p className={`text-base font-medium ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
              {error}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={getSubscription}
                className="py-4 px-8 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-widest rounded-2xl transition-all duration-300 active:scale-95"
              >
                Try again
              </button>
              <button
                onClick={() => navigate("/profile")}
                className={`py-4 px-8 font-bold text-xs uppercase tracking-widest rounded-2xl transition-all duration-300 active:scale-95 border-2 ${
                  theme === 'dark'
                    ? 'bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-700'
                    : 'bg-white text-slate-600 border-slate-100 hover:bg-slate-50 shadow-sm'
                }`}
              >
                Back to profile
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoItem label="Program" value={subscription?.program} theme={theme} />
              <InfoItem label="Billing Period" value={subscription?.period} theme={theme} />
              <InfoItem label="Provider" value={subscription?.provider} theme={theme} />
              <div className={`flex flex-col gap-1 ${
                theme === 'dark' ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-100'
              } border rounded-2xl p-4 transition-all duration-300`}>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${
                  theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
                }`}>Status</span>
                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest w-fit mt-1 ${
                  subscription?.status === "Trialing" 
                    ? "bg-yellow-500/10 text-yellow-500 border border-yellow-500/20"
                    : "bg-green-500/10 text-green-500 border border-green-500/20"
                }`}>
                  {subscription?.status}
                </span>
              </div>
              <div className="sm:col-span-2">
                <InfoItem 
                  label="Expires At" 
                  value={subscription?.expired_at ? new Date(subscription?.expired_at).toLocaleString() : "N/A"} 
                  theme={theme} 
                />
              </div>
            </div>

            <div className={`p-6 rounded-2xl border-2 border-dashed ${theme === 'dark' ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`text-base sm:text-lg font-medium text-center ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
                Do you want to continue unsubscribing from this program?
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={handleYes}
                className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xs uppercase tracking-widest rounded-2xl shadow-lg shadow-red-600/20 transition-all duration-300 active:scale-95"
              >
                Yes, Continue
              </button>
              <button
                onClick={handleNo}
                className={`flex-1 py-4 font-bold text-xs uppercase tracking-widest rounded-2xl transition-all duration-300 active:scale-95 border-2 ${
                  theme === 'dark' 
                    ? 'bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-700' 
                    : 'bg-white text-slate-600 border-slate-100 hover:bg-slate-50 shadow-sm'
                }`}
              >
                No, Go Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionList;
